import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { subscribe } from "@/lib/api";

const FALLBACK_ERROR = "Something went sideways. Mind trying again?";

/**
 * Safely extracts a human-readable string from any error shape.
 * FastAPI/Pydantic validation errors return `detail` as an array of
 * objects (e.g. [{ msg, loc, type }]) rather than a string, which would
 * otherwise crash React when passed directly as a toast message.
 */
function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first.msg === "string") return first.msg;
  }
  return FALLBACK_ERROR;
}

/**
 * Reusable MailerLite-ready subscribe form.
 * Backend handles MailerLite directly (or queues locally until keys arrive).
 */
export default function SubscribeForm({
  source = "newsletter",
  buttonLabel = "Subscribe",
  placeholder = "your@email.com",
  namePlaceholder = "First name",
  withName = true,
  testIdPrefix = "subscribe",
  theme = "light", // "light" or "dark"
  // Optional Tailwind gap class between the email input and submit
  // button. Defaults to "gap-3" (12px), preserving existing behavior
  // for all other callers unless explicitly overridden.
  emailButtonGapClassName = "gap-3",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const isDark = theme === "dark";

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribe({ email, name, source });
      setDone(true);
      toast.success(res?.message || "You're in. Welcome.");
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={`rounded-2xl p-5 ${
          isDark
            ? "bg-white/10 border border-white/20 text-white"
            : "bg-[#159E9A]/8 border border-[#159E9A]/25 text-[#1C1917]"
        }`}
      >
        <p className="font-display text-xl">A little light, on its way.</p>
        <p className={`text-sm mt-1 ${isDark ? "text-white/80" : "text-[#57534E]"}`}>
          We saved your spot. Watch your inbox soon.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      className="flex flex-col gap-3"
    >
      {withName && (
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={namePlaceholder}
          data-testid={`${testIdPrefix}-name`}
          className={`w-full rounded-full px-5 py-3.5 text-sm outline-none transition ${
            isDark
              ? "bg-white/10 placeholder-white/60 text-white border border-white/25 focus:bg-white/20"
              : "bg-white border border-[#E7E5E4] focus:border-[#159E9A]"
          }`}
        />
      )}
      <div className={`flex flex-col sm:flex-row ${emailButtonGapClassName}`}>
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-email`}
          className={`flex-1 rounded-full px-5 py-3.5 text-sm outline-none transition ${
            isDark
              ? "bg-white/10 placeholder-white/60 text-white border border-white/25 focus:bg-white/20"
              : "bg-white border border-[#E7E5E4] focus:border-[#159E9A]"
          }`}
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testIdPrefix}-submit`}
          className={`group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed ${
            isDark
              ? "bg-[#F6A041] text-[#1C1917] hover:bg-[#f3ad5d]"
              : "bg-[#FF5A4F] text-white hover:bg-[#e8463c]"
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Sending…
            </>
          ) : (
            <>
              {buttonLabel}{" "}
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </>
          )}
        </button>
      </div>
      <p className={`text-[11px] ${isDark ? "text-white/60" : "text-[#57534E]/80"}`}>
        No spam. Unsubscribe anytime.
      </p>
    </form>
  );
}
